/**
 * RobotPictureContext
 *
 * Robot pictures live in a PRIVATE S3 bucket, so they can only be rendered through
 * a presigned URL that expires. Signing happens once, here: `GET
 * /api/portal/robots/pictures` returns { robotId -> signed URL } for every robot
 * this portal user can see that has its own uploaded picture, and every consumer
 * (RobotPicture) reads that map by id. Mirrors botriq-admin-fe's
 * RobotPictureContext.
 */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import CONFIG from "../config/config";
import { apiFetch } from "../services/http";
import { useAuth } from "../auth/AuthProvider";

const RobotPictureContext = createContext({ pictures: {}, refreshPictures: () => {} });

// The server signs for 6 hours. Refreshing at 5 leaves an hour of headroom, so a
// long-lived tab never renders a URL that expired while it sat open.
const REFRESH_MS = 5 * 60 * 60 * 1000;

export const RobotPictureProvider = ({ children }) => {
  const [pictures, setPictures] = useState({});
  // Guards against a refresh landing after unmount and setting state on a dead
  // component — the interval and the manual refresh can overlap.
  const aliveRef = useRef(true);
  // This provider wraps the whole app, including /login. The endpoint is
  // authenticated, so it must not be called before a token exists.
  const { token } = useAuth();

  const refreshPictures = useCallback(async () => {
    const portalToken = localStorage.getItem("portalToken");
    if (!portalToken) {
      setPictures({});
      return;
    }
    try {
      const res = await apiFetch(`${CONFIG.BASE_URL}/api/portal/robots/pictures`, {
        headers: { Authorization: `Bearer ${portalToken}` },
      });
      if (!aliveRef.current || !res.ok) return;
      const data = await res.json();
      setPictures(data?.pictures || {});
    } catch {
      // A picture is decoration: if signing fails, every consumer already falls
      // back to the generic bot type picture or initials. Failing loudly here
      // would put an error on screens whose actual content loaded fine.
    }
  }, []);

  useEffect(() => {
    aliveRef.current = true;
    refreshPictures();
    const id = setInterval(refreshPictures, REFRESH_MS);
    return () => {
      aliveRef.current = false;
      clearInterval(id);
    };
  }, [refreshPictures, token]);

  const value = useMemo(() => ({ pictures, refreshPictures }), [pictures, refreshPictures]);

  return <RobotPictureContext.Provider value={value}>{children}</RobotPictureContext.Provider>;
};

RobotPictureProvider.propTypes = { children: PropTypes.node };

/**
 * `{ pictures, refreshPictures }` — `pictures` is keyed by robot id.
 */
export const useRobotPictures = () => useContext(RobotPictureContext);

export default RobotPictureContext;
